import React, { useCallback, useEffect } from 'react';
import { LocalVideoConfig } from './LocalConfig';
import { NumberField } from '../atomic/forms';
import { CheckBox, SlideSwitch } from '../atomic/switches';
import { VideoToImageMode } from '../../models/AiProcessModel';

export interface LocalConfigVideoFieldsProps {
    video: LocalVideoConfig;
    setVideo: (cb: (opts: LocalVideoConfig) => LocalVideoConfig) => void;
    disable: boolean;
    showSliceSwitch: boolean;
}
export const useLocalConfigVideoFields = ({
    video,
    setVideo,
    disable,
    showSliceSwitch,
}: LocalConfigVideoFieldsProps) => {
    // 追加項目なので保存されてない場合はデフォルトをセット
    useEffect(() => {
        if (!('isSlicing' in video)) {
            setVideo((opts) => ({
                ...opts,
                isSlicing: false,
            }));
        }
    }, []);

    const setSliceFlg = useCallback(
        (sliceFlg: boolean) => {
            setVideo((opts) => ({
                ...opts,
                sliceFlg: sliceFlg,
            }));
        },
        [setVideo]
    );

    const setIsSlicing = useCallback(
        (isSlicing: boolean) => {
            setVideo((opts) => ({
                ...opts,
                isSlicing: isSlicing,
            }));
        },
        [setVideo]
    );

    const setKeyFrameMode = useCallback(
        (isKeyframeMode: boolean) => {
            setVideo((opts) => ({
                ...opts,
                sliceTime: 10,
                mode: isKeyframeMode
                    ? VideoToImageMode.KEYFRAMES
                    : VideoToImageMode.MS,
            }));
        },
        [setVideo]
    );

    const setSliceTime = useCallback(
        (sliceTime: string) =>
            setVideo((opts) => ({
                ...opts,
                sliceTime: parseInt(sliceTime),
            })),
        [setVideo]
    );
    const setSliceM = useCallback(
        (sliceM: string) =>
            setVideo((opts) => ({
                ...opts,
                sliceM: parseInt(sliceM),
            })),
        [setVideo]
    );
    const setUseSliceM = useCallback(
        (useSliceM: boolean) =>
            setVideo((opts) => ({
                ...opts,
                useSliceM,
            })),
        [setVideo]
    );
    const setRemoveVideo = useCallback(
        (removeVideo: boolean) =>
            setVideo((opts) => ({
                ...opts,
                removeVideo,
            })),
        [setVideo]
    );
    return (
        <ul>
            <>
                <li className="atomic atomic slide-switch-box">
                    <SlideSwitch
                        defaultValue={video.isSlicing}
                        name={'動画を分割する'}
                        text={'動画を分割する'}
                        _onChange={setIsSlicing}
                        disabled={disable}
                    />
                </li>
            </>

            {video.isSlicing && (
                <>
                    <li className="select-container">
                        <SlideSwitch
                            defaultValue={
                                video.mode === VideoToImageMode.KEYFRAMES
                            }
                            name="use-keyframes"
                            text="キーフレームモード"
                            _onChange={setKeyFrameMode}
                            disabled={disable}
                        />

                        <NumberField
                            label="スライス間隔（ミリ秒）"
                            required
                            name="slice-time"
                            value={String(video.sliceTime)}
                            min={10}
                            step={10}
                            max={200000}
                            _onChange={setSliceTime}
                            disabled={
                                disable ||
                                video.mode === VideoToImageMode.KEYFRAMES
                            }
                        />
                    </li>
                    <li className="atomic checkbox-group">
                        <SlideSwitch
                            name={'aspect-enabled'}
                            text={'一定距離以上離れたフレームのみ保存する'}
                            defaultValue={video.useSliceM}
                            _onChange={setUseSliceM}
                            disabled={disable}
                        />
                    </li>
                    <li className="select-container">
                        <NumberField
                            label="フレームをスライスする距離の閾値（メートル）"
                            name="slice-in-m"
                            required
                            min={1}
                            max={200000}
                            step={1}
                            value={String(video.sliceM || 1)}
                            _onChange={setSliceM}
                            disabled={!video.useSliceM || disable}
                        />
                    </li>
                    <li
                        className="atomic checkbox-group"
                        style={{ marginBottom: '1em' }}
                    >
                        <SlideSwitch
                            name={'remove-video'}
                            text={'分割完了後に動画ファイルを削除する'}
                            defaultValue={video.removeVideo ?? false}
                            _onChange={setRemoveVideo}
                            disabled={disable}
                        />
                    </li>
                </>
            )}
        </ul>
    );
};
